/**
 * Eigenbau: Große dünne Kennzahl — Tremors Kennzahlen sitzen in Karten.
 *
 * Der Entwurf zeigt die Leitzahl einer Seite in 62px, Gewicht 200, mit
 * Ziffern auf gleicher Breite und einem Mono-Label darüber. Kein Rahmen, keine
 * Karte, kein Symbol.
 *
 * Im Bestand liegt nichts dafür bereit: Tremors `Card` wäre der Rahmen, den der
 * Entwurf gerade weglässt, `ProgressCircle` und `CategoryBar` zeigen Anteile,
 * nicht eine Zahl. Die vorhandene `Kennzahl` in `ProjektDetail.tsx` ist ein
 * geteiltes Band aus vier gleichrangigen Werten — eine andere Bauform für eine
 * andere Aussage.
 *
 * Die Nullregel gilt über `zahlenton` aus `lib/entwurf-zustaende`: Eine Null
 * tritt zurück und nimmt **nie** die Warnfarbe. Deshalb nimmt dieser Baustein
 * keine Farbe als Prop, sondern eine Absicht (`ton`) — wer eine Farbe
 * durchreichen könnte, würde die Regel irgendwann umgehen.
 */

import { Link } from "@tanstack/react-router";
import { cx, focusRing } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { zahlenton } from "@/lib/entwurf-zustaende";

const GRADE = {
  46: "text-[46px]",
  56: "text-[56px]",
  62: "text-[62px]",
} as const;

export function Kennzahl({
  label,
  wert,
  grad = 62,
  ton,
  zu,
  className,
}: {
  label: string;
  wert: number | string;
  grad?: keyof typeof GRADE;
  /**
   * Wohin die Zahl führt — Suchparameter für `/aufgaben`.
   *
   * Ohne `zu` bleibt sie stumm. Eine Leitzahl, die die dringlichste Arbeit
   * benennt und keinen Weg dorthin bietet, ist die teuerste Sackgasse einer
   * Einstiegsseite.
   */
  zu?: Record<string, string> | undefined;
  /**
   * Ohne `ton` bleibt die Zahl Tinte. Mit `ton` entscheidet die Nullregel:
   * `0` tritt zurück, alles darüber meldet sich.
   */
  ton?: "warn" | "akzent";
  className?: string;
}) {
  const farbe =
    ton && typeof wert === "number" ? zahlenton(wert, ton) : "text-tinte";

  /*
   * Die Affordanz sitzt auf dem Label, nicht auf der Zahl.
   *
   * Verlinkt war die Leitzahl schon, sichtbar war es nicht: ein `block` mit
   * Fokusring und sonst nichts — ohne Zeiger nicht auffindbar. Eine
   * Haarlinie unter eine 62px-Ziffer zu legen scheidet aus, dort wäre 1px ein
   * Kratzer. Das Mono-Label darüber ist ohnehin das Register, in dem der
   * Entwurf seine Verweise führt, und trägt sie in genau derselben Form wie
   * `Verweis` und `Nebenwert`. So gilt im ganzen Band eine Regel statt drei.
   */
  const inhalt = (
    <>
      <Mikrolabel
        grad={12}
        className={cx(
          zu && [
            "underline decoration-linie decoration-1 underline-offset-[3px]",
            "transition-colors duration-150 ease-aus",
            "group-hover/kennzahl:decoration-akzent",
          ],
        )}
      >
        {label}
      </Mikrolabel>
      <div
        className={cx(
          "mt-1 font-extralight leading-[0.9] tracking-kennzahl tabular-nums",
          GRADE[grad],
          farbe,
        )}
      >
        {wert}
      </div>
    </>
  );

  if (zu) {
    return (
      <Link
        to="/aufgaben"
        search={zu}
        className={cx("group/kennzahl block", focusRing, className)}
      >
        {inhalt}
      </Link>
    );
  }

  return <div className={className}>{inhalt}</div>;
}
